import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Badge } from '../components/ui/badge'
import { Progress } from '../components/ui/progress'
import { useToast } from '../components/ui/use-toast'
import { mailingListService, MailingListEntry, MailingListStats } from '../services/mailingListService'
import { Download, Search, Trash2, UserCheck, UserX, RefreshCw, Mail, Edit2, Shuffle, MoreVertical, XCircle } from 'lucide-react'
import { format } from 'date-fns'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Textarea } from '../components/ui/textarea'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu'

export default function MailingListPage() {
  const [entries, setEntries] = useState<MailingListEntry[]>([])
  const [stats, setStats] = useState<MailingListStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterSignupType, setFilterSignupType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterInviteStatus, setFilterInviteStatus] = useState<string>('all')
  const [selectedEntries, setSelectedEntries] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalEntries, setTotalEntries] = useState(0)
  const [notesDialogOpen, setNotesDialogOpen] = useState(false)
  const [createInviteDialogOpen, setCreateInviteDialogOpen] = useState(false)
  const [inviteDetailsDialogOpen, setInviteDetailsDialogOpen] = useState(false)
  const [currentEntry, setCurrentEntry] = useState<MailingListEntry | null>(null)
  const [notesText, setNotesText] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [inviteMaxUses, setInviteMaxUses] = useState<string>('100')
  const [inviteType, setInviteType] = useState<'manual' | 'random'>('manual')
  const [currentInviteDetails, setCurrentInviteDetails] = useState<any>(null)
  const [selectedForInvite, setSelectedForInvite] = useState<string[]>([])
  const { toast } = useToast()

  const entriesPerPage = 50

  useEffect(() => {
    loadData()
  }, [currentPage, filterSignupType, filterStatus])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Load stats
      const statsData = await mailingListService.getStats()
      setStats(statsData)
      
      // Load entries with filters
      const params: any = {
        limit: entriesPerPage,
        offset: (currentPage - 1) * entriesPerPage
      }
      
      if (filterSignupType !== 'all') {
        params.signupType = filterSignupType
      }
      
      if (filterStatus !== 'all') {
        params.status = filterStatus
      }
      
      const { entries: entriesData, total } = await mailingListService.getEntries(params)
      setEntries(entriesData)
      setTotalEntries(total)
    } catch (error) {
      console.error('Failed to load mailing list data:', error)
      toast({
        title: 'Error',
        description: 'Failed to load mailing list data',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const params: any = {}
      if (filterSignupType !== 'all') params.signupType = filterSignupType
      if (filterStatus !== 'all') params.status = filterStatus
      
      const blob = await mailingListService.exportEntries(format, params)
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `mailing-list-${format}-${new Date().toISOString()}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      toast({
        title: 'Export Successful',
        description: `Mailing list exported as ${format.toUpperCase()}`
      })
    } catch (error) {
      console.error('Export failed:', error)
      toast({
        title: 'Export Failed',
        description: 'Failed to export mailing list',
        variant: 'destructive'
      })
    }
  }

  const handleBulkStatusUpdate = async (status: 'active' | 'unsubscribed') => {
    if (selectedEntries.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select entries to update',
        variant: 'destructive'
      })
      return
    }

    try {
      await mailingListService.bulkUpdateStatus(selectedEntries, status)
      
      toast({
        title: 'Update Successful',
        description: `${selectedEntries.length} entries updated to ${status}`
      })
      
      setSelectedEntries([])
      loadData()
    } catch (error) {
      console.error('Bulk update failed:', error)
      toast({
        title: 'Update Failed',
        description: 'Failed to update selected entries',
        variant: 'destructive'
      })
    }
  }

  const handleDeleteEntry = async (id: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) return
    
    try {
      await mailingListService.deleteEntry(id)
      
      toast({
        title: 'Entry Deleted',
        description: 'Mailing list entry has been deleted'
      })
      
      loadData()
    } catch (error) {
      console.error('Delete failed:', error)
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete entry',
        variant: 'destructive'
      })
    }
  }

  const toggleEntrySelection = (id: string) => {
    setSelectedEntries(prev =>
      prev.includes(id)
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (selectedEntries.length === entries.length) {
      setSelectedEntries([])
    } else {
      setSelectedEntries(entries.map(entry => entry.id))
    }
  }

  const handleBulkInviteAction = async (action: 'mark_sent' | 'mark_accepted' | 'mark_declined') => {
    if (selectedEntries.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select entries to update',
        variant: 'destructive'
      })
      return
    }

    try {
      const statusMap = {
        mark_sent: 'sent',
        mark_accepted: 'accepted',
        mark_declined: 'declined'
      } as const

      await mailingListService.bulkUpdateInviteStatus(selectedEntries, statusMap[action])
      toast({
        title: 'Status Updated',
        description: `${selectedEntries.length} entries marked as ${statusMap[action]}`
      })

      setSelectedEntries([])
      loadData()
    } catch (error) {
      console.error('Bulk invite action failed:', error)
      toast({
        title: 'Action Failed',
        description: 'Failed to update selected entries',
        variant: 'destructive'
      })
    }
  }

  const openNotesDialog = (entry: MailingListEntry) => {
    setCurrentEntry(entry)
    setNotesText(entry.adminNotes || '')
    setNotesDialogOpen(true)
  }

  const handleSaveNotes = async () => {
    if (!currentEntry) return

    try {
      await mailingListService.updateAdminNotes(currentEntry.id, notesText)

      toast({
        title: 'Notes Saved',
        description: 'Admin notes updated successfully'
      })

      setNotesDialogOpen(false)
      loadData()
    } catch (error) {
      console.error('Save notes failed:', error)
      toast({
        title: 'Save Failed',
        description: 'Failed to save notes',
        variant: 'destructive'
      })
    }
  }

  const handleSingleInviteStatusChange = async (
    entryId: string,
    status: 'not_sent' | 'sent' | 'accepted' | 'declined'
  ) => {
    try {
      await mailingListService.updateInviteStatus(entryId, status)

      const statusLabels = {
        not_sent: 'cleared',
        sent: 'sent',
        accepted: 'accepted',
        declined: 'declined'
      }

      toast({
        title: 'Status Updated',
        description: `Invite status ${statusLabels[status]}`
      })

      loadData()
    } catch (error) {
      console.error('Update invite status failed:', error)
      toast({
        title: 'Update Failed',
        description: 'Failed to update invite status',
        variant: 'destructive'
      })
    }
  }

  const generateRandomCode = () => {
    // Generate a random 8-character code
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // Removed ambiguous characters
    let code = ''
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return code
  }

  const openCreateInviteDialog = () => {
    setSelectedForInvite([...selectedEntries])
    setInviteCode('')
    setInviteMaxUses('100')
    setInviteType('manual')
    setCreateInviteDialogOpen(true)
  }

  const handleInviteTypeChange = (type: 'manual' | 'random') => {
    setInviteType(type)
    if (type === 'random') {
      setInviteCode(generateRandomCode())
      setInviteMaxUses('1') // Single use for random codes
    } else {
      setInviteCode('')
      setInviteMaxUses('100') // Default for manual codes
    }
  }

  const handleCreateInvite = async () => {
    if (!inviteCode.trim()) {
      toast({
        title: 'Code Required',
        description: 'Please enter an invite code',
        variant: 'destructive'
      })
      return
    }

    try {
      const invite = await mailingListService.createCampaignInvite({
        code: inviteCode.trim().toUpperCase(),
        maxUses: parseInt(inviteMaxUses) || undefined
      })

      // Link invite to selected entries
      if (selectedForInvite.length > 0) {
        await Promise.all(
          selectedForInvite.map(entryId =>
            mailingListService.linkInvite(entryId, invite.id)
          )
        )
      }

      toast({
        title: 'Invite Created',
        description: `Code: ${invite.code} - Linked to ${selectedForInvite.length} entries`
      })

      setCurrentInviteDetails(invite)
      setCreateInviteDialogOpen(false)
      setInviteDetailsDialogOpen(true)
      setSelectedEntries([])
      setSelectedForInvite([])
      loadData()
    } catch (error: any) {
      console.error('Create invite failed:', error)
      toast({
        title: 'Creation Failed',
        description: error.response?.data?.message || 'Failed to create invite',
        variant: 'destructive'
      })
    }
  }

  const viewInviteDetails = async (inviteId: string) => {
    try {
      const details = await mailingListService.getInviteDetails(inviteId)
      setCurrentInviteDetails(details)
      setInviteDetailsDialogOpen(true)
    } catch (error) {
      console.error('Failed to load invite details:', error)
      toast({
        title: 'Load Failed',
        description: 'Failed to load invite details',
        variant: 'destructive'
      })
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast({
      title: 'Copied',
      description: 'Copied to clipboard'
    })
  }

  const filteredEntries = entries.filter(entry => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      const matchesSearch = (
        entry.email.toLowerCase().includes(search) ||
        entry.firstName?.toLowerCase().includes(search) ||
        entry.lastName?.toLowerCase().includes(search) ||
        entry.source?.toLowerCase().includes(search) ||
        entry.inviteId?.toLowerCase().includes(search) ||
        entry.adminNotes?.toLowerCase().includes(search)
      )
      if (!matchesSearch) return false
    }

    if (filterInviteStatus !== 'all' && entry.inviteStatus !== filterInviteStatus) {
      return false
    }

    return true
  })

  const totalPages = Math.ceil(totalEntries / entriesPerPage)

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>
      case 'unsubscribed':
        return <Badge variant="secondary">Unsubscribed</Badge>
      case 'bounced':
        return <Badge variant="destructive">Bounced</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getSignupTypeBadge = (type: string) => {
    switch (type) {
      case 'beta':
        return <Badge className="bg-blue-500">Beta</Badge>
      case 'newsletter':
        return <Badge className="bg-purple-500">Newsletter</Badge>
      case 'updates':
        return <Badge className="bg-orange-500">Updates</Badge>
      default:
        return <Badge>{type}</Badge>
    }
  }

  const getInviteStatusBadge = (status: string) => {
    switch (status) {
      case 'not_sent':
        return <Badge variant="secondary">Not Sent</Badge>
      case 'sent':
        return <Badge className="bg-blue-500">Sent</Badge>
      case 'accepted':
        return <Badge className="bg-green-500">Accepted</Badge>
      case 'declined':
        return <Badge variant="destructive">Declined</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Mailing List Management</h1>
        <Button onClick={() => loadData()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <Progress value={(stats.active / stats.total) * 100} className="mt-2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Beta Signups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.beta}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Newsletter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{stats.newsletter}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Unsubscribed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">{stats.unsubscribed}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Actions</CardTitle>
          <CardDescription>Filter and manage your mailing list entries</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, name, or source..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={filterSignupType} onValueChange={setFilterSignupType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Signup Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="beta">Beta</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="updates">Updates</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterInviteStatus} onValueChange={setFilterInviteStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Invite Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Invites</SelectItem>
                <SelectItem value="not_sent">Not Sent</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => handleExport('csv')} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => handleExport('json')} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            {selectedEntries.length > 0 && (
              <>
                <Button 
                  onClick={() => handleBulkStatusUpdate('active')} 
                  variant="outline" 
                  size="sm"
                  className="text-green-600"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Mark Active ({selectedEntries.length})
                </Button>
                <Button
                  onClick={() => handleBulkStatusUpdate('unsubscribed')}
                  variant="outline"
                  size="sm"
                  className="text-orange-600"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Mark Unsubscribed ({selectedEntries.length})
                </Button>
                <div className="w-full border-t my-2" />
                <Button
                  onClick={() => handleBulkInviteAction('mark_sent')}
                  variant="outline"
                  size="sm"
                  className="text-blue-600"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Mark Invited ({selectedEntries.length})
                </Button>
                <Button
                  onClick={() => handleBulkInviteAction('mark_accepted')}
                  variant="outline"
                  size="sm"
                  className="text-green-600"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Mark Accepted ({selectedEntries.length})
                </Button>
                <div className="w-full border-t my-2" />
                <Button
                  onClick={openCreateInviteDialog}
                  variant="default"
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Create & Link Invite ({selectedEntries.length})
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mailing List Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mailing List Entries</CardTitle>
          <CardDescription>
            Showing {filteredEntries.length} of {totalEntries} total entries
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <input
                        type="checkbox"
                        checked={selectedEntries.length === entries.length && entries.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invite Status</TableHead>
                    <TableHead>Invite ID</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Signed Up</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedEntries.includes(entry.id)}
                          onChange={() => toggleEntrySelection(entry.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{entry.email}</TableCell>
                      <TableCell>
                        {entry.firstName || entry.lastName
                          ? `${entry.firstName || ''} ${entry.lastName || ''}`.trim()
                          : '-'}
                      </TableCell>
                      <TableCell>{getSignupTypeBadge(entry.signupType)}</TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell>{getInviteStatusBadge(entry.inviteStatus)}</TableCell>
                      <TableCell>
                        {entry.inviteId ? (
                          <Button
                            onClick={() => viewInviteDetails(entry.inviteId!)}
                            variant="ghost"
                            size="sm"
                            className="font-mono text-xs p-1 h-auto"
                            title="Click to view invite details"
                          >
                            ...{entry.inviteId.slice(-8)}
                          </Button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => openNotesDialog(entry)}
                          variant="ghost"
                          size="sm"
                          title={entry.adminNotes || 'Add notes'}
                        >
                          <Edit2 className="h-4 w-4" />
                          {entry.adminNotes && <span className="ml-1 text-xs">({entry.adminNotes.substring(0, 20)}...)</span>}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {format(new Date(entry.createdAt), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openNotesDialog(entry)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit Notes
                            </DropdownMenuItem>
                            {entry.inviteId && (
                              <DropdownMenuItem onClick={() => viewInviteDetails(entry.inviteId!)}>
                                <Mail className="h-4 w-4 mr-2" />
                                View Invite Details
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleSingleInviteStatusChange(entry.id, 'sent')}
                              disabled={entry.inviteStatus === 'sent'}
                            >
                              <UserCheck className="h-4 w-4 mr-2" />
                              Mark Invite Sent
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleSingleInviteStatusChange(entry.id, 'accepted')}
                              disabled={entry.inviteStatus === 'accepted'}
                            >
                              <UserCheck className="h-4 w-4 mr-2" />
                              Mark Invite Accepted
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleSingleInviteStatusChange(entry.id, 'not_sent')}
                              disabled={entry.inviteStatus === 'not_sent'}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Clear Invite Status
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteEntry(entry.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Entry
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admin Notes</DialogTitle>
            <DialogDescription>
              Add notes for {currentEntry?.email}. Track invite replies, codes sent, or any other relevant information.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Enter notes here..."
              rows={6}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNotes}>
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invite Dialog */}
      <Dialog open={createInviteDialogOpen} onOpenChange={setCreateInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Campaign Invite</DialogTitle>
            <DialogDescription>
              Create a new invite code{selectedForInvite.length > 0 && ` and link to ${selectedForInvite.length} selected entries`}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Invite Type Toggle */}
            <div>
              <label className="block text-sm font-medium mb-2">Invite Type</label>
              <div className="inline-flex rounded-md shadow-sm w-full" role="group">
                <Button
                  type="button"
                  variant={inviteType === 'manual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInviteTypeChange('manual')}
                  className="flex-1 rounded-r-none"
                >
                  <Edit2 className="h-4 w-4 mr-1.5" />
                  Manual
                </Button>
                <Button
                  type="button"
                  variant={inviteType === 'random' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInviteTypeChange('random')}
                  className="flex-1 rounded-l-none border-l-0"
                >
                  <Shuffle className="h-4 w-4 mr-1.5" />
                  Random
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {inviteType === 'manual'
                  ? 'Create a custom code for multiple users'
                  : 'Generate a unique code for one person'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Invite Code</label>
              <div className="flex gap-2">
                <Input
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder={inviteType === 'manual' ? 'BETA2024' : 'Auto-generated'}
                  className="font-mono flex-1"
                  disabled={inviteType === 'random'}
                />
                {inviteType === 'random' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setInviteCode(generateRandomCode())}
                    title="Generate new code"
                  >
                    <Shuffle className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {inviteType === 'manual'
                  ? 'This will be the code users enter to sign up'
                  : 'Click the shuffle button to generate a new code'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Max Uses</label>
              <Input
                type="number"
                value={inviteMaxUses}
                onChange={(e) => setInviteMaxUses(e.target.value)}
                placeholder={inviteType === 'manual' ? '100' : '1'}
                disabled={inviteType === 'random'}
              />
              <p className="text-xs text-gray-500 mt-1">
                {inviteType === 'manual'
                  ? 'Leave empty for unlimited uses'
                  : 'Single-use codes are automatically set to 1'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateInvite} className="bg-blue-600 hover:bg-blue-700">
              Create Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Details Dialog */}
      <Dialog open={inviteDetailsDialogOpen} onOpenChange={setInviteDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invite Details</DialogTitle>
            <DialogDescription>
              Copy and share this invite code and URL with users.
            </DialogDescription>
          </DialogHeader>
          {currentInviteDetails && (
            <div className="py-4 space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invite Code</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border font-mono text-lg font-bold">
                      {currentInviteDetails.code}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(currentInviteDetails.code)}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invite URL</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border font-mono text-sm break-all">
                      {currentInviteDetails.url}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(currentInviteDetails.url)}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-gray-600">Max Uses</label>
                  <p className="font-medium">{currentInviteDetails.maxUses || 'Unlimited'}</p>
                </div>
                <div>
                  <label className="block text-gray-600">Used Count</label>
                  <p className="font-medium">{currentInviteDetails.usedCount || 0}</p>
                </div>
                <div>
                  <label className="block text-gray-600">Status</label>
                  <p className="font-medium">{currentInviteDetails.isActive ? 'Active' : 'Inactive'}</p>
                </div>
                <div>
                  <label className="block text-gray-600">Type</label>
                  <p className="font-medium capitalize">{currentInviteDetails.type}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInviteDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}